import {
  clamp,
  clamp01,
  createAdvancedVoice,
  createBodyResonatorBank,
  createNoiseBurst,
  hashSeed,
  scheduleSafeEnvelope
} from "./dspPrimitives";
import { getVariantModelProfile } from "./instrumentModelProfiles";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, ResonatorSpec } from "./types";

function controlNumber(input: AdvancedSynthesisInput, id: string, fallback: number): number {
  const value = input.controls[id];

  return typeof value === "number" ? clamp01(value) : fallback;
}

function makeFrictionCurve(context: BaseAudioContext, pressure: number): WaveShaperNode {
  const shaper = context.createWaveShaper();
  const curve = new Float32Array(1024);
  const drive = 2.2 + pressure * 12;

  for (let index = 0; index < curve.length; index += 1) {
    const x = (index / (curve.length - 1)) * 2 - 1;
    const stick = Math.tanh(x * drive);
    const slip = Math.sin(x * Math.PI * (1.3 + pressure * 1.2)) * 0.08 * pressure;

    curve[index] = stick * (0.58 + pressure * 0.24) + slip;
  }

  shaper.curve = curve;
  shaper.oversample = "2x";

  return shaper;
}

function erhuFormants(frequency: number, variantId?: string): ResonatorSpec[] {
  const nasalOffset = variantId === "erhu_hexagonal" ? 120 : variantId === "erhu_rare_circular" ? -120 : 0;
  const width = variantId === "erhu_octagonal" ? 0.8 : 1;

  return [
    { frequency: clamp(frequency * 2.5 + nasalOffset, 700, 1100), q: 4.8 * width, gainDb: 4.8, type: "peaking" },
    { frequency: clamp(frequency * 4.2 + nasalOffset, 1150, 1800), q: 3.6 * width, gainDb: 4.2, type: "peaking" },
    { frequency: variantId === "erhu_rare_circular" ? 2450 : 3100, q: 2.2, gainDb: 2.8, type: "peaking" },
    { frequency: variantId === "erhu_hexagonal" ? 4300 : 3700, q: 1.5, gainDb: 1.8, type: "peaking" }
  ];
}

export function renderErhu(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("erhu", input.variantId);
  const pressure = controlNumber(input, "bowPressure", 0.5);
  const speed = controlNumber(input, "bowSpeed", 0.45);
  const rouXian = input.ornament === "rou_xian" || input.ornament === "portamento";
  const gain = context.createGain();
  const frictionBus = context.createGain();
  const oscillator = context.createOscillator();
  const companion = context.createOscillator();
  const friction = makeFrictionCurve(context, pressure);
  const toneFilter = context.createBiquadFilter();
  const bodyInput = context.createGain();
  const amplitudeLfo = context.createOscillator();
  const amplitudeLfoGain = context.createGain();
  const sources: AudioScheduledSourceNode[] = [oscillator, companion, amplitudeLfo];
  const cleanupNodes: AudioNode[] = [gain, frictionBus, friction, toneFilter, bodyInput, amplitudeLfoGain];
  const attack = 0.018 + (1 - speed) * 0.055 + (variant?.attack ?? 0.5) * 0.018;
  const brightness = clamp01((variant?.brightness ?? 0.55) + pressure * 0.24);
  const bowNoise = createNoiseBurst(context, {
    duration: Math.max(0.9, input.duration ?? 1.2),
    attack: 0.02,
    decay: 0.55,
    gain: pressure * (0.018 + (variant?.noiseGain ?? 0.4) * 0.048),
    filterType: "bandpass",
    frequency: 2100 + pressure * 2900,
    q: 1.2 + pressure * 2.4,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "bow-scrape"),
    tilt: "bright",
    startTime: input.now
  });

  gain.gain.value = 0.0001;
  scheduleSafeEnvelope(gain.gain, context, {
    attack,
    decay: 0.12 + (1 - speed) * 0.08,
    sustain: 0.72 + pressure * 0.18,
    release: 0.34,
    peakGain: input.velocity * (0.38 + pressure * 0.24),
    startTime: input.now
  });

  oscillator.type = "sawtooth";
  companion.type = "triangle";
  oscillator.frequency.setValueAtTime(input.frequency, input.now);
  companion.frequency.setValueAtTime(input.frequency * 2.002, input.now);
  companion.detune.setValueAtTime(-6 + pressure * 4, input.now);
  toneFilter.type = "lowpass";
  toneFilter.frequency.setValueAtTime(1050 + brightness * 4300, input.now);
  toneFilter.Q.setValueAtTime(0.9 + pressure * 1.4, input.now);
  frictionBus.gain.value = 0.72;
  bodyInput.gain.value = 0.82;

  oscillator.connect(friction);
  companion.connect(friction);
  friction.connect(toneFilter);
  toneFilter.connect(frictionBus);
  frictionBus.connect(bodyInput);
  bodyInput.connect(gain);
  bowNoise.output.connect(gain);

  cleanupNodes.push(...bowNoise.cleanupNodes);
  sources.push(bowNoise.source);
  cleanupNodes.push(...createBodyResonatorBank(context, bodyInput, gain, erhuFormants(input.frequency, input.variantId), variant?.bodyGain ?? 0.7));

  amplitudeLfo.type = "sine";
  amplitudeLfo.frequency.setValueAtTime(rouXian ? 5.4 + pressure * 1.4 : 1.7 + speed * 1.2, input.now);
  amplitudeLfoGain.gain.setValueAtTime(rouXian ? 0.055 + pressure * 0.05 : 0.018, input.now);
  amplitudeLfo.connect(amplitudeLfoGain);
  amplitudeLfoGain.connect(gain.gain);

  oscillator.detune.setValueAtTime(0, input.now);
  oscillator.detune.linearRampToValueAtTime(rouXian ? 18 : 5 + pressure * 9, input.now + 0.12);
  oscillator.detune.linearRampToValueAtTime(rouXian ? -12 : -4, input.now + 0.24);
  oscillator.detune.linearRampToValueAtTime(rouXian ? 9 : 2, input.now + 0.36);
  companion.detune.linearRampToValueAtTime(rouXian ? 12 : 4, input.now + 0.18);
  toneFilter.frequency.linearRampToValueAtTime(rouXian ? 2200 + brightness * 4200 : 1200 + brightness * 3800, input.now + 0.18);
  toneFilter.frequency.linearRampToValueAtTime(1050 + brightness * 4300, input.now + 0.42);

  if (input.ornament === "portamento") {
    const endHz = input.frequency * 1.12246;
    const fingerNoise = createNoiseBurst(context, {
      duration: 0.22,
      attack: 0.02,
      decay: 2.2,
      gain: input.velocity * 0.016,
      filterType: "bandpass",
      frequency: 1800,
      q: 1.1,
      seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "finger-slide"),
      tilt: "bright",
      startTime: input.now + 0.04
    });

    oscillator.frequency.linearRampToValueAtTime(endHz, input.now + 0.34);
    companion.frequency.linearRampToValueAtTime(endHz * 2.002, input.now + 0.34);
    fingerNoise.output.connect(gain);
    fingerNoise.source.start(input.now + 0.04);
    sources.push(fingerNoise.source);
    cleanupNodes.push(...fingerNoise.cleanupNodes);
  }

  oscillator.start(input.now);
  companion.start(input.now);
  amplitudeLfo.start(input.now);
  bowNoise.source.start(input.now);

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.32,
    debug: {
      family: "bowed_string_friction",
      model: "erhu_friction_membrane_formant",
      variantTags: variant?.metadataTags,
      layers: ["nonlinear_friction", "bow_scrape", "membrane_formants", rouXian ? "rou_xian_brightness_amplitude" : "bow_jitter"]
    }
  });
}
